// components/QuoteForm.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import axios from 'axios';

const QuoteForm = ({ open, onClose, designData, selectedColors, selectedSizes, quantities }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Generate order number
      const orderNumber = `ZAR-${Date.now()}`;
      
      // Calculate total amount using design price
      const designPrice = designData.price || 29.99;
      const totalAmount = Object.values(quantities).reduce((sum, qty) => sum + (qty * designPrice), 0);

      const quoteData = {
        order_number: orderNumber,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        design_id: designData.id,
        design_name: designData.title,
        design_category: designData.category,
        selected_colors: selectedColors,
        selected_sizes: selectedSizes,
        quantities: quantities,
        total_amount: totalAmount,
        notes: formData.notes
      };

      const response = await axios.post('/api/quotes', quoteData);
      
      if (response.data.success) {
        setSuccess('Quote submitted successfully! You will receive an email with your quote shortly.');
        setFormData({
          customer_name: '',
          customer_email: '',
          customer_phone: '',
          customer_address: '',
          notes: ''
        });
        
        // Close dialog after 3 seconds
        setTimeout(() => {
          onClose();
          setSuccess('');
        }, 3000);
      } else {
        setError('Failed to submit quote. Please try again.');
      }

    } catch (error) {
      console.error('Error submitting quote:', error);
      setError(error.response?.data?.error || 'Failed to submit quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError('');
      setSuccess('');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Submit Quote Request
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fill in your details to get a custom quote for your t-shirt design
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Design:</strong> {designData?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Category:</strong> {designData?.category}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Colors:</strong> {Object.entries(selectedColors || {}).map(([key, value]) => (
              <Chip key={key} label={`${key}: ${value}`} size="small" sx={{ mr: 1, mb: 1 }} />
            ))}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Sizes & Quantities:</strong> {Object.entries(quantities || {}).map(([size, qty]) => (
              <Chip key={size} label={`${size}: ${qty}`} size="small" sx={{ mr: 1, mb: 1 }} />
            ))}
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                name="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address (Optional)"
                name="customer_address"
                value={formData.customer_address}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                disabled={loading}
                placeholder="Any special requirements or notes for your order..."
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.customer_name || !formData.customer_email}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Submitting...' : 'Submit Quote'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteForm;
